import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, Mic, MapPin, ArrowRight, Wallet } from "lucide-react";
import { toast } from "sonner";
import { TOURS, formatINR } from "@/data/tours";
import { NAV_LINKS } from "@/data/content";
import { searchTours, isExactMatch, norm, parseBudget, describeBudget, getDeal } from "@/lib/search";

// Destination names (states, cities, regions) from the India / World mega menus
const DESTINATIONS = NAV_LINKS.filter((l) => l.mega?.kind === "destinations").flatMap((l) =>
  l.mega.regions.flatMap((r) => [r.name, ...r.groups.flatMap((g) => [g.state, ...g.cities])])
    .map((name) => ({ name: name.replace(/^All of /, ""), route: l.route, scope: l.label }))
).filter((d, i, arr) => arr.findIndex((x) => x.name === d.name) === i);

const findDestinations = (q) => {
  const n = norm(q);
  if (n.length < 2) return [];
  return DESTINATIONS.filter((d) => ` ${norm(d.name)}`.includes(` ${n}`)).slice(0, 4);
};

const useVoice = (onResult) => {
  const [listening, setListening] = useState(false);
  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice search isn't supported in this browser. Please type instead.");
      return;
    }
    const rec = new SR();
    rec.lang = "en-IN";
    rec.onresult = (e) => onResult(e.results[0][0].transcript);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    setListening(true);
    rec.start();
  };
  return { listening, start };
};

export const SearchBox = ({ variant = "desktop", onDone }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const isMobile = variant === "mobile";
  const tid = isMobile ? "mobile-search" : "header-search";

  useEffect(() => {
    if (location.pathname === "/search") setValue(new URLSearchParams(location.search).get("q") || "");
  }, [location.pathname, location.search]);

  useEffect(() => {
    const close = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const tours = useMemo(() => {
    if (norm(value).length < 2 || !isExactMatch(TOURS, value, { prefix: true })) return [];
    return searchTours(TOURS, value, { prefix: true }).slice(0, 5);
  }, [value]);
  const budget = useMemo(() => parseBudget(value), [value]);
  const destQuery = (name) => (budget.label ? `${name} ${budget.label}` : name);
  const dests = useMemo(
    () => findDestinations(budget.text).filter((d) => !budget.label || (isExactMatch(TOURS, `${d.name} ${budget.label}`) && searchTours(TOURS, `${d.name} ${budget.label}`).length > 0)),
    [budget]
  );

  const go = (path) => {
    setOpen(false);
    navigate(path);
    onDone?.();
  };
  const submit = (q = value) => q.trim() && go(`/search?q=${encodeURIComponent(q.trim())}`);
  const voice = useVoice((text) => { setValue(text); submit(text); });

  const showPanel = open && norm(value).length >= 2;

  return (
    <div ref={wrapRef} className="relative">
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className={`flex items-center gap-2 border border-gray-300 rounded-full px-4 ${isMobile ? "py-2.5" : "py-2"} hover:border-[var(--yellow-2)] focus-within:border-[var(--yellow-2)] transition`}
        role="search"
      >
        <button type="submit" aria-label="search" data-testid={`${tid}-submit`} className="text-gray-400 hover:text-[var(--navy)]">
          <Search className="w-4 h-4" />
        </button>
        <input
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          data-testid={`${tid}-input`}
          placeholder={isMobile ? "Search tours & destinations" : 'Search for "Cherry Blossom"'}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm placeholder:text-gray-400"
        />
        {!isMobile && (
          <button type="button" onClick={voice.start} aria-label="voice search" data-testid={`${tid}-mic`}>
            <Mic className={`w-4 h-4 ${voice.listening ? "text-[var(--red)] animate-pulse" : "text-gray-400 hover:text-[var(--navy)]"}`} />
          </button>
        )}
      </form>

      {showPanel && (
        <div data-testid={`${tid}-suggestions`} className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-[0_24px_50px_-20px_rgba(11,37,69,0.45)] border border-gray-100 z-[60] overflow-hidden text-[var(--navy)]">
          {budget.label && (
            <div className="flex items-center gap-2 px-4 py-2 bg-[#fff8e7] border-b border-gray-100 text-[12px] text-[var(--navy)]" data-testid={`${tid}-budget`}>
              <Wallet className="w-3.5 h-3.5 text-[var(--yellow-2)]" />
              Budget: <b>{describeBudget(budget)}</b>
              {budget.text && <span className="text-gray-500">· {budget.text}</span>}
            </div>
          )}
          {dests.length > 0 && (
            <div className="py-2 border-b border-gray-100">
              <div className="px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-gray-400 font-semibold">Destinations</div>
              {dests.map((d, i) => (
                <button key={d.name} type="button" onClick={() => go(`${d.route}?q=${encodeURIComponent(destQuery(d.name))}`)} data-testid={`${tid}-dest-${i}`} className="w-full flex items-center gap-2.5 px-4 py-2 text-left text-[13px] hover:bg-[var(--soft)]">
                  <MapPin className="w-3.5 h-3.5 text-[var(--yellow-2)]" />
                  <span className="flex-1">{d.name}</span>
                  <span className="text-[11px] text-gray-400">{d.scope}</span>
                </button>
              ))}
            </div>
          )}
          {tours.length > 0 && (
            <div className="py-2 border-b border-gray-100">
              <div className="px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-gray-400 font-semibold">Tour packages</div>
              {tours.map((t) => { const deal = getDeal(t); return (
                <button key={t.slug} type="button" onClick={() => go(`/tour/${t.slug}`)} data-testid={`${tid}-tour-${t.slug}`} className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-[var(--soft)]">
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[13px] font-medium truncate">{t.title}</span>
                      {deal && <span className="shrink-0 bg-[var(--red)] text-white text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" data-testid={`${tid}-deal-${t.slug}`}>Deal</span>}
                    </span>
                    <span className="block text-[11px] text-gray-400 truncate">{t.days} Days · {t.region}</span>
                  </span>
                  <span className="shrink-0 text-right" data-testid={`${tid}-tour-price-${t.slug}`}>
                    {deal ? (
                      <span className="block text-[10.5px] text-gray-500 line-through leading-none" data-testid={`${tid}-old-price-${t.slug}`}>{formatINR(deal.oldPrice)}</span>
                    ) : (
                      t.startingPrice != null && <span className="block text-[10px] text-gray-400 leading-none">Starts from</span>
                    )}
                    <span className={`block font-bold ${t.startingPrice != null ? "text-[13px] text-[var(--navy)]" : "text-[11px] text-gray-500"}`}>{formatINR(t.startingPrice)}</span>
                  </span>
                </button>
              ); })}
            </div>
          )}
          {dests.length === 0 && tours.length === 0 && (
            <div className="px-4 py-3 text-[13px] text-gray-500" data-testid={`${tid}-empty`}>{budget.label ? `No tours match “${value}”. Try a higher budget.` : `No exact matches for “${value}”. Press enter to see the closest tours.`}</div>
          )}
          <button type="button" onClick={() => submit()} data-testid={`${tid}-see-all`} className="w-full flex items-center justify-between px-4 py-2.5 text-[13px] font-semibold bg-[var(--soft)] hover:bg-[#fcd34d]/20">
            See all results for “{value}” <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
